import Backbone from 'backbone'
import _ from 'lodash'
import calendars from 'scoreboard/components/calendars'
import colorIcon from 'scoreboard/components/color-icon'
import config from 'scoreboard/components/config'
import constants from 'scoreboard/components/constants'
import formatValue from 'scoreboard/components/format-value'
import i18n from 'scoreboard/components/i18n'
import ChartData from 'scoreboard/charts/chart-data/chart-data'
import prepareData from 'scoreboard/charts/chart-data/prepare-data'
import ChartDesigner from 'scoreboard/charts/model/chart-designer'
import unwrapModel from 'scoreboard/components/unwrap-model'
import template from './historical-table.hbs'

/**
 * For PDF Export - renders a table where each row represents a period
 * containing its score and metric values (if applicable)
 */
var HistoricalTable = Backbone.View.extend({

	tagName: 'table',
	className: 'table historical-table scalable',

	/**
	 * @param {Object} options.model A Scorecard model
	 * @param {Object} options.fullContext A context object from scorecard-context.js
	 * @param {ChartDesigner} options.chartDesigner
	 */
	initialize: function (options) {
		this.options = options || {}
		var ctx = this.options.fullContext

		const {chart, chartData} = unwrapModel(this.model)
		this.data = new ChartData({data: chartData})
		this.chartDesigner = new ChartDesigner(chart, {data: this.data})

		// assigns the formatters to each serie, among other things
		const preparedData = prepareData({
			chart: this.chartDesigner,
			chartData: this.data
		})

		// For scorecard overviews there is only ever a single plot
		const plot = preparedData.plots[0]
		this.range = plot.breakdown.range
		this.serieFeatures = this.chartDesigner.getSerieFeatures()
		// Need to ignore all analytics, we only care about data from serie
		this.series = _.filter(plot.series, s =>
			s.id === 'default-serie' || _.some(this.serieFeatures, f =>  f.id == s.id))
		this.isSingleSeries = _.size(this.serieFeatures) == 1

		var currencyCode = ctx.currencyCode
		var dataType = ctx.dataType
		const scores = _.get(this.data.scorecards(this.model.id), 'scores')

		var columnLabels = this._constructColumnLabels(ctx)
		var rowData = this._constructRowData(ctx, scores, dataType, currencyCode)

		this.context = {
			columnLabels: columnLabels,
			rowData: rowData
		}
	},

	render: function () {
		this.$el.empty().append(template(this.context))
		this.$el.data('associated-chart', '.export-historical .chart-container')
		this.$el.data('top-table-element', '.row')
		return this
	},

	/**
	 * Single Series - Score, Actual, and Threshold Keys (if present)
	 * Multi Series - Values Only
	 *
	 * @ctx - full context (scorecards-context)
	 *
	 * @private
	 */
	_constructColumnLabels: function (ctx) {
		var columnLabels = []
		var isMetric = ctx.metricish
		const {dataProperty, thresholdIndex} = _.result(this.serieFeatures, '[0].attributes.source', {})

		/**
		 * To simplify testing only set charted on the single label object
		 * that should be marked (bolded) in the header.
		 * @param {object} label
		 * @param {String|Number} dataIdentifier a field name OR the threshold index
		 * @returns label updated with the charted attribute if necessary
		 */
		var setCharted = function (label, dataIdentifier) {
			if (('threshold' == dataProperty && thresholdIndex == dataIdentifier) ||
				dataProperty == dataIdentifier) {
				label.charted = true
			}
			return label
		}

		// Single series are score and actual with no color
		if (this.isSingleSeries) {
			if (this._showScore(isMetric)) {
				columnLabels.push(setCharted({
					label: i18n.get('common.score')}, 'score'))
			}

			isMetric && columnLabels.push(setCharted({
				label: i18n.get('scorecards.node.metric.actual')}, 'value'))

		} else { // Multi-series are labeled with color
			_.forEach(this.series, ({title: label, color}) => {
				columnLabels.push({
					label,
					seriesColorIcon: colorIcon(color)
				})
			})
		}
		// Threshold Labels (Single Series Only)
		var includeThresholds = this.isSingleSeries && ctx.scoringType
		// #49640 don't show threshold columns for custom text scoring types
		if (isMetric && includeThresholds && !this.isCustomText(ctx.scoringType)) {
			_.forEach(ctx.scoringType.thresholdNames, (k, thresholdIndex) => {
				columnLabels.push(setCharted({ label: i18n.get(k) }, thresholdIndex))
			})
		}
		return columnLabels
	},

	/**
	 * Transform raw model data into a format iterable for PDF overview template
	 *
	 * @param {Boolean} ctx.metricish if this scorecard item is a metric or linked metric
	 * @param {Score[]} scores The scores, including threshold values
	 * @param {String} [dataType] The scorecard dataType, used for formatting
	 * @param {String} [currencyCode] The scorecard currency code
	 *
	 * @returns An array of map objects for each row, containing:
	 * periodLabel, scores, thresholds
	 *
	 * @private
	 */
	_constructRowData: function (ctx, scores, dataType, currencyCode) {
		const {metricish, scoringType} = ctx
		const scoreByPeriod = _.keyBy(scores, 'periodId')
		return _.map(this.range, function (r, periodIndex) {
			const {periodId} = unwrapModel(r)
			const score = scoreByPeriod[periodId]
			let seriesData = []
			let thresholds = []

			//Score is First
			if (this.isSingleSeries) {
				var cellData = {}
				if (score) {
					cellData.value = formatValue({ dataType: 'score', value: score.score})
					if (!metricish) {
						cellData.color = score.color
					}
				}
				this._showScore(metricish) && seriesData.push(cellData)
			}

			//Then Series scores ('Actual' for single series)
			if (metricish || !this.isSingleSeries) {
				_.forEach(this.series, s => {
					const point = s.points[periodIndex]
					let value
					if (_.isUndefined(point)) {
						seriesData.push({})
					} else {
						let valueToUse = this.isSingleSeries ? score && score.actual : point.raw
						// #49641 for custom text scoring types, we don't want to
						// use the same formatter that was applied for use by the
						// chart (since it retrieves thresholds by score, see
						// scorecard-formatter.js)
						if (this.isCustomText(scoringType)) {
							// instead, just format the value, which will be the
							// id of a custom threshold, as the threshold label
							value = formatValue.formatThreshold(valueToUse)
						} else {
							value = s.formatter(valueToUse)
						}
						seriesData.push({
							color: metricish ? point.color : undefined,
							value
						})
					}
				})
			}

			if (metricish && this.isSingleSeries) {
				//Then thresholds when single-series
				if (score) {
					thresholds = _.map(score.thresholds, function (t) {
						return formatValue({
							dataType: dataType,
							value: t,
							currencyCode: currencyCode})
					})
				} else {
					// #49640 don't show threshold columns for custom text scoring types
					if (!this.isCustomText(scoringType)) {
						thresholds = _.map(scoringType.thresholdNames, () => '')
					}
				}
			}

			return {
				periodLabel: calendars.periodsById[periodId].label,
				seriesData: seriesData,
				thresholds: thresholds
			}
		}.bind(this))
	},

	_showScore: function(isMetric) {
		// https://github.com/SpiderStrategies/Scoreboard/issues/13276#issuecomment-330652175
		return !isMetric || config.scoresVisible
	},

	isCustomText(scoringType) {
		// #58106 #58107 use scoringType.isCustomText instead of
		// scoringType.customText
		return _.get(scoringType, 'isCustomText', false)
	},

	chartHasExportedScorecardItem () {
		let seriesForThisScorecardItem = this.chartDesigner.getSerieFeatures().filter(s => {
			return s.domainType() == 'scorecard-item'
				&& s.domainObjectId() == this.model.id
		})

		return seriesForThisScorecardItem.length > 0
	}

})

export default HistoricalTable

HistoricalTable.__filename__ = 'scorecards/export/historical-table.js'
